import { JobOffer, JobOfferResponse, JobReview, MessageSummary, User_ChatRoomView } from '@dinosband/bv-main-schema/dist/types'
import { Broadcast } from '@dinosband/bv-messenger-schema'
import { SmartSubscribe } from '@dinosband/bv-main-client'
import { BvSubscriber, SubscriptionManager, JobOfferResponseSubscriber, JobOfferSubscriber, MessageSummarySubscriber, MessengerBroadcastSubscriber, User_ChatRoomViewSubscriber, BvDataCenter } from '@dinosband/bv-data-center'
import { JOB_REVIEW_ADDED } from '@dinosband/bv-data-center'
import { waitFor, _first } from '@dinosband/dbi-utils'

import { apolloClient } from '../..'
import { ChatRoomContext } from '../../controller-pool'

const _DEBUG_LOG = process.env.NODE_ENV !== 'production' &&
                   !!process.env.VUE_APP_MESSENGER_DEBUG_LOG

export interface ChatRoomDataSubscriberDelegate {
  // onUserViewUpdated (userView: User_ChatRoomView): void
  // onMessageSummaryUpdated (messageSummary: MessageSummary): void
  // onJobOfferUpdated (jobSummary: JobOffer): void
  // onJobOfferResponseUpdated (jobOfferResponse: JobOfferResponse): void
  onBroadcast (broadcast: Broadcast): void
}

export class ChatRoomDataSubscriber {

  chatRoomId: number
  started = false
  delegate!: ChatRoomDataSubscriberDelegate

  private context: ChatRoomContext

  userView!: User_ChatRoomViewSubscriber
  messageSummary!: MessageSummarySubscriber
  broadcast!: MessengerBroadcastSubscriber
  jobOffer!: JobOfferSubscriber
  jobOfferResponse!: JobOfferResponseSubscriber
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private subscribers!: BvSubscriber<any>[]
  private subscriptions = new SubscriptionManager()

  constructor (context: ChatRoomContext, dataCenter: BvDataCenter) {
    this.context = context
    this.chatRoomId = context.chatRoomId
    this.initSubscribers(dataCenter)
  }

  initSubscribers (dataCenter: BvDataCenter) {
    this.userView = new User_ChatRoomViewSubscriber(this.context, (value) => {
      this.onUserViewUpdated(value)
      // this.delegate.onUserViewUpdated(value)
    })
    this.messageSummary = new MessageSummarySubscriber(this.chatRoomId, (value) => {
      this.onMessageSummaryUpdated(value)
      // this.delegate.onMessageSummaryUpdated(value)
    })
    const context_ = { messengerClient: apolloClient, chatRoomId: this.chatRoomId }
    this.broadcast = new MessengerBroadcastSubscriber(context_, this.chatRoomId, (value) => {
      this.delegate.onBroadcast(value)
    })
    this.jobOffer = new JobOfferSubscriber(this.context.mainStore, undefined, this.context.jobOfferId, (value) => {
      this.onJobOfferUpdated(value)
    })
    this.jobOfferResponse = new JobOfferResponseSubscriber(this.context, (value) => {
      this.onJobOfferResponseUpdated(value)
    })
    this.subscriptions.init(dataCenter.context.pubsub)
    this.subscriptions.subscribe(JOB_REVIEW_ADDED, (value: JobReview) => {
      if (value.chatRoomId === this.chatRoomId) {
        this.onJobReviewUpdated(value)
      }
    })
    this.subscribers = [this.userView, this.messageSummary, this.jobOffer, this.jobOfferResponse, this.broadcast]
  }

  cleanup () {
    this.stop()
    this.subscriptions.cleanup()
    this.subscribers.forEach((subscriber) => subscriber.cleanup())
    this.subscribers = []
  }

  start () {
    this.subscribers.forEach((subscriber) => subscriber.start())
    this.started = true
  }

  stop () {
    this.started = false
    this.subscribers.forEach((subscriber) => subscriber.stop())
  }

  // reconnect () {
  //   this.stop()
  //   this.start()
  // }

  async retrieve (mesgIds: number[]) {
    const subscribers_ = this.subscribers.filter((i) => i !== this.messageSummary)
    await Promise.all([
      this.messageSummary.retrieve(mesgIds),
      ...subscribers_.map((i) => i.retrieve()),
    ])
  }

  async waitMessageSummary (messageId: number, timeout: number) {
    let found: MessageSummary | undefined = this.context.store.messageSummaries[messageId]
    if (found) {
      return found
    }
    let subscription: SmartSubscribe | undefined
    if (!this.started) {
      subscription = await this.context.mainStore.subscribeOnMessageSummaryUpdated(this.chatRoomId, (value) => {
        this.onMessageSummaryUpdated(value)
      })
    }
    /* subscription 걸기 전에 이미 summary가 생성된 경우 updated로 들어오지 않는다
       그래서 이미 생성되어 있는지 query를 한번 해본다 */
    found = _first(await this.context.mainStore.readMessageSummaries([messageId]))
    await waitFor(() => {
      found = found || this.context.store.messageSummaries[messageId]
      return !!found
    }, timeout)
    subscription?.unsubscribe()
    if (!found) {
      // 어떤 이유로 message summary를 가져오지 못했다. 서버에 한번 더 query 해보자.
      found = _first(await this.context.mainStore.readMessageSummaries([messageId]))
    }
    return found
  }

  private onUserViewUpdated (value: User_ChatRoomView | User_ChatRoomView[]) {
    let userViews: User_ChatRoomView[] = []
    if (value instanceof Array) {
      userViews = value
    } else {
      userViews = [value]
    }
    const others = userViews.filter((i) => i.userId !== this.context.mainStore.userId)
    others.forEach((i) => { this.context.store.updateUserReadMessageId(i) })
  }

  private onMessageSummaryUpdated (value: MessageSummary | MessageSummary[]) {
    let summaries: MessageSummary[] = []
    if (value instanceof Array) {
      summaries = value
    } else {
      summaries = [value]
    }
    summaries.forEach((i) => {
      if (!i.updatedBy || i.updatedBy === this.context.userId) {
        return
      }
      const prev = this.context.store.messageSummaries[i.id]
      i.liked = prev?.liked ?? false
      i.bookmarked = prev?.bookmarked ?? false
    })
    this.context.store.addMessageSummaries(summaries)
  }

  private onJobOfferUpdated (jobOffer: JobOffer) {
    this.context.store.jobOffer = jobOffer
    this.context.store.isMyJobOffer = this.context.mainStore.profileIds.includes(jobOffer.clientId)
  }

  private onJobOfferResponseUpdated (jrsp: JobOfferResponse) {
    this.context.store.jobOfferResponse = jrsp
  }

  private onJobReviewUpdated (value: JobReview) {
    const jrsp = this.context.store.jobOfferResponse
    if (!jrsp) {
      return
    }
    if (jrsp.id === value.jobAsClientId) {
      jrsp.reviewByClient = value
    } else if (jrsp.id === value.jobAsProviderId) {
      jrsp.reviewByProvider = value
    }
    this.context.store.jobOfferResponse = Object.assign({}, jrsp)
  }

}
